/**
 * ComingSoonModal — pre-launch waitlist modal.
 *
 * Fires from every borrower-side "Start Application" / "Get My Readiness Score"
 * CTA while LK_FEATURES.applicationLive is false. Captures full_name + email
 * + property_state into the early_access_signups table.
 *
 * Designed to feel friendly, not gate-keepy: honest about the launch timing,
 * makes the visitor feel like they're getting in early rather than turned away.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import { supabase, isSupabaseConfigured, friendlyErrorMessage } from '../../lib/supabase';
import { US_STATES } from '../../data/usStates';

const ComingSoonModal = ({ open, onClose, source = 'welcome_page' }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const firstFieldRef = useRef(null);

  // Reset form when reopened (so a closed-after-success modal is fresh next time)
  useEffect(() => {
    if (open) {
      setError(null);
      // small delay to let the modal mount before focusing
      const t = setTimeout(() => firstFieldRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
    if (!open && success) {
      // Clear after closing the success state
      const t = setTimeout(() => {
        setSuccess(false);
        setFullName('');
        setEmail('');
        setState('');
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open, success]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isValid = fullName.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(email) && state.length === 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;

    // Backend not wired up yet — fail gracefully instead of letting the
    // browser surface a bare TypeError: Failed to fetch.
    if (!isSupabaseConfigured) {
      setError(friendlyErrorMessage());
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const url = new URL(window.location.href);
      const payload = {
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        property_state: state,
        source,
        user_agent: navigator.userAgent.slice(0, 400),
        referrer_url: document.referrer || null,
        utm_source:   url.searchParams.get('utm_source'),
        utm_medium:   url.searchParams.get('utm_medium'),
        utm_campaign: url.searchParams.get('utm_campaign'),
      };

      const { error: dbError } = await supabase
        .from('early_access_signups')
        .insert([payload]);

      if (dbError) {
        if (dbError.code === '23505' || /duplicate/i.test(dbError.message || '')) {
          setSuccess(true);
          // Resend their confirmation anyway — idempotency key dedupes.
          fireConfirmationEmail(payload);
          return;
        }
        throw dbError;
      }
      setSuccess(true);
      fireConfirmationEmail(payload);
    } catch (err) {
      console.error('Early access signup failed', err);
      setError(friendlyErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Fire-and-forget so a Resend hiccup never blocks the success UI.
  // The Edge Function dedupes via its own idempotency key.
  const fireConfirmationEmail = (payload) => {
    supabase.functions
      .invoke('send-signup-confirmation', {
        body: {
          kind: 'borrower',
          fullName: payload.full_name,
          email: payload.email,
          propertyState: payload.property_state,
          source: payload.source,
        },
      })
      .then(({ error: fnError }) => {
        if (fnError) console.warn('Confirmation email failed:', fnError.message);
      })
      .catch((e) => console.warn('Confirmation email exception:', e));
  };

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="coming-soon-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        backgroundColor: 'rgba(10,10,10,0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, animation: 'lk-modal-fade 180ms ease-out',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%', maxWidth: 480,
          backgroundColor: 'white', borderRadius: 18,
          boxShadow: '0 30px 80px rgba(0,0,0,0.30)',
          padding: 32, paddingTop: 28,
          animation: 'lk-modal-pop 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 36, height: 36, borderRadius: '50%',
            border: 'none', backgroundColor: 'transparent',
            cursor: 'pointer', color: '#777',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f4f4f5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <X size={18} />
        </button>

        {success ? (
          <SuccessState onClose={onClose} state={state} />
        ) : (
          <>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: 'linear-gradient(135deg, #1a3aff 0%, #6366f1 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 18,
              boxShadow: '0 8px 24px rgba(26, 58, 255, 0.25)',
            }}>
              <Sparkles size={26} color="white" />
            </div>

            <h2 id="coming-soon-title" style={{
              fontSize: 24, fontWeight: 800, lineHeight: 1.2,
              margin: 0, marginBottom: 8, color: '#0a0a0a',
              letterSpacing: '-0.01em',
            }}>
              Owen's almost ready.
            </h2>
            <p style={{
              fontSize: 15, color: '#555', lineHeight: 1.55,
              margin: 0, marginBottom: 22,
            }}>
              We're putting the finishing touches on the LoanKnow application.
              Drop your details and we'll let you know the moment Owen opens
              the door — usually a few weeks before public launch in your state.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <Field label="Your name">
                <input
                  ref={firstFieldRef}
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Smith"
                  style={inputStyle}
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  style={inputStyle}
                />
              </Field>
              <Field label="Property state">
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  style={{ ...inputStyle, paddingRight: 32, appearance: 'none', backgroundImage: 'linear-gradient(45deg, transparent 50%, #999 50%), linear-gradient(135deg, #999 50%, transparent 50%)', backgroundPosition: 'calc(100% - 16px) 50%, calc(100% - 11px) 50%', backgroundSize: '5px 5px, 5px 5px', backgroundRepeat: 'no-repeat' }}
                >
                  <option value="">Select your state…</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </Field>

              {error && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '10px 12px', borderRadius: 8,
                  backgroundColor: '#fee2e2', color: '#b91c1c',
                  fontSize: 13, lineHeight: 1.4, marginBottom: 14,
                }}>
                  <AlertCircle size={16} style={{ marginTop: 1, flexShrink: 0 }} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={!isValid || submitting}
                style={{
                  width: '100%', display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                  backgroundColor: isValid && !submitting ? '#0a0a0a' : '#a3a3a3',
                  color: 'white', padding: '14px 22px',
                  fontSize: 15, fontWeight: 700,
                  borderRadius: 999, border: 'none',
                  cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
                  marginTop: 6,
                  transition: 'background-color 140ms ease',
                }}
              >
                {submitting ? 'Adding you…' : 'Get on the list'}
                {!submitting && <ArrowRight size={16} />}
              </button>

              <p style={{
                fontSize: 12, color: '#999', textAlign: 'center',
                marginTop: 14, marginBottom: 0, lineHeight: 1.5,
              }}>
                One email when we open. Never sold. Never shared.
              </p>
            </form>
          </>
        )}
      </div>

      <style>{`
        @keyframes lk-modal-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lk-modal-pop {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </div>
  );

  return createPortal(modal, document.body);
};

const SuccessState = ({ onClose, state }) => {
  const stateName = US_STATES.find((s) => s.code === state)?.name || 'your state';
  return (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        backgroundColor: '#dcfce7',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16,
      }}>
        <CheckCircle size={32} color="#16a34a" />
      </div>
      <h2 style={{
        fontSize: 22, fontWeight: 800, lineHeight: 1.25,
        margin: 0, marginBottom: 8, color: '#0a0a0a',
      }}>
        You're on the list.
      </h2>
      <p style={{
        fontSize: 15, color: '#555', lineHeight: 1.55,
        margin: 0, marginBottom: 24, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto',
      }}>
        We'll email you the second LoanKnow opens up in {stateName}. In the
        meantime, keep an eye out — we send one note when we're live, and
        nothing else.
      </p>
      <button
        onClick={onClose}
        style={{
          backgroundColor: 'transparent', color: '#0a0a0a',
          border: '1px solid #e5e5e5', borderRadius: 999,
          padding: '11px 24px', fontSize: 14, fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Done
      </button>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <div style={{
      fontSize: 12, fontWeight: 600,
      color: '#444', marginBottom: 6,
    }}>
      {label}
    </div>
    {children}
  </label>
);

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  fontSize: 15,
  border: '1px solid #e5e5e5',
  borderRadius: 10,
  backgroundColor: '#fafafa',
  color: '#0a0a0a',
  outline: 'none',
  transition: 'border-color 140ms ease, background-color 140ms ease',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

export default ComingSoonModal;
